import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { DEADLINE_ERROR, validateDeadline } from '../../settings-recall-agent/types';
import type { RecallAgentSettings } from '../../settings-recall-agent/types';

export interface RecallSettingsViewProps {
  value: RecallAgentSettings;
  location: string;
  /** Derived from the same list `/recall` renders — the two must agree. */
  recallTotal: number;
  recallRunning: number;
  recallHref?: string;
}

/**
 * Settings › Recall (amendment 20). **A link strip and two cards.**
 *
 * The page previously carried nine items, of which one was a Recall setting, and
 * its copy was the design reasoning rather than anything a practice could act on —
 * *"The one rung no other agent has"*, *"which is not very much, and the page is
 * short because of it"*. **Every visible string here is new.**
 */
export function RecallSettingsView({
  value,
  location,
  recallTotal,
  recallRunning,
  recallHref = '/recall',
}: RecallSettingsViewProps) {
  return (
    <div>
      <SettingsPageHeader
        eyebrow="Settings · AI Brain"
        titleLead="Recall"
        titleAccent="settings"
        sub="Settings that apply to every recall. To create or change a recall, go to Recall."
      />

      {/* The link strip. Its counts come from the same data as the /recall list,
          so the two screens cannot disagree — which they did. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 rounded-[18px] bg-[var(--brand-indigo-tint)] px-6 py-5">
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-[var(--text-primary)]">
            Your recalls are in Recall
          </div>
          <p className="m-0 mt-1.5 max-w-[620px] text-[12.5px] leading-[1.65] text-[var(--text-secondary)]">
            {recallTotal} recalls · {recallRunning} running. Create, edit, pause and see results
            there.
          </p>
        </div>
        {/* White text on the accent fill — the old label was dark on dark. */}
        <a
          href={recallHref}
          className="shrink-0 rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2.5 text-[13.5px] font-semibold !text-white"
        >
          Open Recall
        </a>
      </div>

      <SettingsCards>
        <HumanCallCard value={value} />

        {/* 02 — where the moved settings went. Every link lands on the named
            section of a real page, never the top of a long one. */}
        <SettingsCard
          n="02"
          title="Related settings"
          sub="These affect Recall but are set once, for every agent."
        >
          <RelatedRow
            label="Voice and personality"
            desc="How ELVA sounds in every text, email and call."
            links={[{ label: 'AI Brain › General', href: '/settings/ai/general#voice' }]}
          />
          {/* Three links, because Channels is a group of three pages rather than a
              page — a single "AI Brain › Channels" link has no honest destination. */}
          <RelatedRow
            label="What ELVA can do on each channel"
            desc="What ELVA may say and do in calls, texts and emails."
            links={[
              { label: 'Calls', href: '/settings/ai/channels/call' },
              { label: 'Texts', href: '/settings/ai/channels/sms' },
              { label: 'Emails', href: '/settings/ai/channels/email' },
            ]}
          />
          <RelatedRow
            label="Combine messages"
            desc="Whether ELVA may send one message instead of two when a patient is due for more than one thing."
            links={[{ label: 'AI Brain › General', href: '/settings/ai/general#combine-messages' }]}
          />
          <RelatedRow
            label="Benefit year-end"
            desc="When patients’ insurance benefits reset. A recall can mention unused benefits in the three months before."
            links={[{ label: 'Insurance & network', href: '/settings/insurance#benefit-year' }]}
          />
          {/* No destination, and that is the honest answer — this one is ELVA's. */}
          <RelatedRow
            last
            label="How often a patient is contacted"
            desc="About six texts every two weeks per phone number — fewer for patients who haven’t visited in a while. Shared by every agent."
          />
        </SettingsCard>
      </SettingsCards>

      {/* The summary line is gone: it counted things this page no longer owns. */}
      <SettingsFooter location={location} />
    </div>
  );
}

/**
 * Card 01 — the page's only setting.
 *
 * **Saves on blur or Enter when valid**, optimistic, with an inline error on
 * failure. An invalid value is never stored: the input keeps what was typed so it
 * can be corrected, and the saved value is left alone.
 */
function HumanCallCard({ value }: { value: RecallAgentSettings }) {
  const [, setSaved] = useState(value.deadlineOpenDays);
  const [draft, setDraft] = useState(String(value.deadlineOpenDays));
  const [error, setError] = useState(false);

  const commit = () => {
    const next = validateDeadline(draft);
    if (next === undefined) {
      setError(true);
      return;
    }
    setError(false);
    setSaved(next);
  };

  return (
    <SettingsCard
      n="01"
      title="Human call steps"
      sub="A recall can include a step where someone from your team calls the patient. When a patient reaches that step, ELVA adds the call to Triage Inbox with who to call, why, and what they’ve already been sent."
    >
      <SettingsRow
        last
        label="Time to make the call"
        desc={
          <>
            If nobody records how the call went within this many days, ELVA marks it{' '}
            <strong className="text-[var(--text-secondary)]">Not attempted</strong> and moves the
            patient to the next step. Only days the practice is open are counted.
            <span className="mt-1 block text-[11.5px] text-[var(--text-muted)]">
              A change applies to new calls. Calls already in Triage Inbox keep their deadline.
            </span>
          </>
        }
        control={
          <span className="inline-block text-right">
            <span className="inline-flex items-center gap-2">
              <input
                value={draft}
                aria-label="Time to make the call, in open days"
                aria-invalid={error}
                inputMode="numeric"
                onChange={(e) => {
                  setDraft(e.target.value.replace(/[^\d]/g, '').slice(0, 2));
                }}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                }}
                className={cn(
                  'w-[70px] rounded-[10px] border bg-[var(--surface-card)] px-3 py-2 text-right text-[13px] text-[var(--text-primary)] outline-none',
                  error
                    ? 'border-[var(--alert-red)]'
                    : 'border-[var(--border-default)] focus:border-[var(--brand-indigo)]'
                )}
              />
              <span className="text-[13px] text-[var(--text-secondary)]">open days</span>
            </span>
            {error ? (
              <span className="mt-1.5 block text-[11px] leading-[1.5] text-[var(--alert-red)]">
                {DEADLINE_ERROR}
              </span>
            ) : null}
          </span>
        }
      />

      {/* The live count, and nothing to click when there is nothing waiting. */}
      <div
        className="px-6 py-[15px] text-[12.5px] leading-[1.6]"
        style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
      >
        {value.callsWaiting === 0 ? (
          'No calls waiting.'
        ) : (
          <>
            <strong className="text-[var(--text-secondary)]">
              {value.callsWaiting} calls waiting now
            </strong>{' '}
            ·{' '}
            <a
              href="/triage?type=recall_call"
              className="font-semibold text-[var(--brand-indigo)] hover:underline"
            >
              Open Triage Inbox →
            </a>
          </>
        )}
      </div>
    </SettingsCard>
  );
}

function RelatedRow({
  label,
  desc,
  links,
  last,
}: {
  label: string;
  desc: string;
  links?: { label: string; href: string }[];
  last?: boolean;
}) {
  return (
    <div
      className="flex items-start justify-between gap-6 px-6 py-[15px]"
      style={last === true ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-[var(--text-primary)]">{label}</div>
        <div className="mt-1 max-w-[620px] text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
          {desc}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
        {links === undefined ? (
          <span className="text-[12.5px] font-semibold text-[var(--text-muted)]">Set by ELVA</span>
        ) : (
          links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
            >
              {l.label} →
            </a>
          ))
        )}
      </div>
    </div>
  );
}
